import type { InfiniteListingController } from "./infinite-listings.js";

export type PropertyReturnState = {
  url: string;
  reference: string;
  viewportOffset: number;
  scrollY: number;
  savedAt: number;
  lastLoadedPage: number;
};

const stateKey = "ncePropertyReturnState";
const pendingKey = "ncePropertyReturnPending";
const lifetime = 6 * 60 * 60 * 1000;

/** Storage contains navigation metadata only, never fetched HTML. */
export function validatePropertyReturnState(value: unknown, origin: string, now = Date.now()): PropertyReturnState | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<PropertyReturnState>;
  if (typeof candidate.url !== "string" || typeof candidate.reference !== "string" || !candidate.reference || candidate.reference.length > 200) return null;
  if (typeof candidate.scrollY !== "number" || !Number.isFinite(candidate.scrollY) || candidate.scrollY < 0) return null;
  if (typeof candidate.viewportOffset !== "number" || !Number.isFinite(candidate.viewportOffset)) return null;
  if (typeof candidate.savedAt !== "number" || !Number.isFinite(candidate.savedAt) || now - candidate.savedAt > lifetime || candidate.savedAt > now + 60_000) return null;
  const lastLoadedPage = candidate.lastLoadedPage ?? 1;
  if (!Number.isSafeInteger(lastLoadedPage) || lastLoadedPage < 1 || lastLoadedPage > 10_000) return null;
  try {
    const url = new URL(candidate.url, origin);
    if (url.origin !== origin || !["https:", "http:"].includes(url.protocol) || url.username || url.password) return null;
    return { url: url.href, reference: candidate.reference, scrollY: candidate.scrollY, viewportOffset: candidate.viewportOffset, savedAt: candidate.savedAt, lastLoadedPage };
  } catch { return null; }
}

function matchesResultsUrl(currentUrl: string, state: PropertyReturnState | null): state is PropertyReturnState {
  if (!state) return false;
  try {
    const current = new URL(currentUrl);
    const target = new URL(state.url);
    return current.origin === target.origin && current.pathname === target.pathname && current.search === target.search;
  } catch { return false; }
}

/** Ordinary visits/reloads do not replay an old search position; bfcache owns its scroll. */
export function selectPropertyReturnState(
  currentUrl: string,
  navigationType: string,
  persisted: boolean,
  pending: PropertyReturnState | null,
  stored: PropertyReturnState | null,
): PropertyReturnState | null {
  if (persisted || navigationType === "reload") return null;
  if (matchesResultsUrl(currentUrl, pending)) return pending;
  return navigationType === "back_forward" && matchesResultsUrl(currentUrl, stored) ? stored : null;
}

export function initPropertyReturn(infinite: InfiniteListingController | null = null): void {
  const read = (key: string): PropertyReturnState | null => {
    try { return validatePropertyReturnState(JSON.parse(sessionStorage.getItem(key) || "null"), window.location.origin); }
    catch { return null; }
  };
  const write = (key: string, state: PropertyReturnState): void => {
    try { sessionStorage.setItem(key, JSON.stringify(state)); } catch { /* Returning by URL still works without storage. */ }
  };
  const removePending = (state: PropertyReturnState): void => {
    const pending = read(pendingKey);
    if (!pending || pending.url !== state.url || pending.reference !== state.reference || pending.savedAt !== state.savedAt) return;
    try { sessionStorage.removeItem(pendingKey); } catch { /* Storage is optional. */ }
  };
  const propertyReference = document.querySelector<HTMLElement>("[data-nce-property-reference]")?.dataset.ncePropertyReference || "";
  const syncBackLinks = (): void => {
    const stored = read(stateKey);
    if (!stored || stored.reference !== propertyReference) return;
    document.querySelectorAll<HTMLAnchorElement>("[data-property-back-to-results]").forEach((link) => { link.href = stored.url; });
  };
  syncBackLinks();

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
    if (!link || link.hasAttribute("download") || (link.target && link.target !== "_self")) return;
    if (link.matches("[data-property-back-to-results]")) {
      const stored = read(stateKey);
      if (stored && stored.reference === propertyReference) {
        link.href = stored.url;
        write(pendingKey, { ...stored, savedAt: Date.now() });
      }
      return;
    }
    const card = link.closest<HTMLElement>(".nce-listing-card[data-property-reference]");
    if (!card || card.closest("[hidden], [inert]")) return;
    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin || !destination.pathname.includes("/property/")) return;
    const state: PropertyReturnState = {
      url: window.location.href,
      reference: card.dataset.propertyReference || "",
      viewportOffset: card.getBoundingClientRect().top,
      scrollY: window.scrollY,
      savedAt: Date.now(),
      lastLoadedPage: infinite?.lastLoadedPage ?? 1,
    };
    write(stateKey, state);
    // A new outbound selection supersedes any earlier unsuccessful return attempt.
    try { sessionStorage.removeItem(pendingKey); } catch { /* Storage is optional. */ }
  });

  let cancelRestore: (() => void) | null = null;
  const restore = async (state: PropertyReturnState): Promise<void> => {
    let cancelled = false;
    let released = false;
    const previousRestoration = history.scrollRestoration;
    const release = (): void => {
      if (released) return;
      released = true;
      for (const eventName of ["wheel", "touchstart", "pointerdown"] as const) window.removeEventListener(eventName, cancel);
      window.removeEventListener("keydown", onKey);
      if ("scrollRestoration" in history) history.scrollRestoration = previousRestoration;
      infinite?.setPaused(false);
      cancelRestore = null;
    };
    const cancel = (): void => { cancelled = true; release(); };
    const onKey = (event: KeyboardEvent): void => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Tab", "Escape"].includes(event.key)) cancel();
    };
    cancelRestore = cancel;
    for (const eventName of ["wheel", "touchstart", "pointerdown"] as const) window.addEventListener(eventName, cancel, { passive: true });
    window.addEventListener("keydown", onKey);
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    infinite?.setPaused(true);
    try {
      // Preserve a retry record if reconstructing a cold history entry fails.
      write(pendingKey, state);
      if (infinite && !(await infinite.ensureReference(state.reference, state.lastLoadedPage))) return;
      if (cancelled) return;
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve())));
      if (cancelled) return;
      const card = Array.from(document.querySelectorAll<HTMLElement>(".nce-listing-card[data-property-reference]"))
        .find((candidate) => candidate.dataset.propertyReference === state.reference && !candidate.closest("[hidden], [inert]"));
      const top = card ? window.scrollY + card.getBoundingClientRect().top - state.viewportOffset : state.scrollY;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: "instant" });
      removePending(state);
    } catch { /* Leave the pending metadata available after a failed reconstruction. */ }
    finally { release(); }
  };

  window.addEventListener("pagehide", () => { cancelRestore?.(); });
  window.addEventListener("pageshow", (event) => {
    syncBackLinks();
    if (!event.persisted) return;
    cancelRestore?.();
    infinite?.setPaused(false);
    const pending = read(pendingKey);
    if (matchesResultsUrl(window.location.href, pending)) removePending(pending);
  });
  const navigationType = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type || "navigate";
  const selected = selectPropertyReturnState(window.location.href, navigationType, false, read(pendingKey), read(stateKey));
  if (selected) void restore(selected);
}
